import sanitizeHtml from "./sanitizeHtml";
import { buildInlineImgTagListFromFiles } from "./inlineImages";

function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function plainTextToHtml(text) {
  return escapeHtml(String(text ?? "").replace(/\r\n?/g, "\n")).replace(/\n/g, "<br>");
}

/**
 * Keep semantic formatting while dropping Word/Docs/browser styling noise.
 * Images and links keep only attributes needed by the editor.
 */
export function normalizeClipboardHtml(rawHtml) {
  const safe = sanitizeHtml(rawHtml || "");
  if (!safe || typeof document === "undefined") return safe;

  const root = document.createElement("div");
  root.innerHTML = safe;

  root.querySelectorAll("*").forEach((node) => {
    const tag = node.tagName.toLowerCase();
    const keep = new Set();

    if (tag === "img") {
      ["src", "alt", "title", "data-img-id", "data-zoom"].forEach((attr) => keep.add(attr));
    } else if (tag === "a") {
      ["href", "title"].forEach((attr) => keep.add(attr));
    } else if (["td", "th"].includes(tag)) {
      ["colspan", "rowspan"].forEach((attr) => keep.add(attr));
    }

    Array.from(node.attributes).forEach((attr) => {
      if (!keep.has(attr.name.toLowerCase())) node.removeAttribute(attr.name);
    });
  });

  return root.innerHTML;
}

function htmlNodeFromTag(tag) {
  if (typeof document === "undefined") return null;
  const holder = document.createElement("div");
  holder.innerHTML = tag;
  return holder.firstElementChild;
}

/**
 * Browsers often expose the same pasted image twice: once in text/html and
 * once as a File. Reuse the HTML image when it already has a safe src; use
 * File-backed data URLs only to fill broken/file:// placeholders, then append
 * genuinely extra files.
 */
export function mergeImageTagsIntoClipboardHtml(baseHtml, imageTags) {
  const tags = Array.from(imageTags || []).filter(Boolean);
  if (!tags.length) return baseHtml || "";
  if (typeof document === "undefined") {
    return [baseHtml, ...tags].filter(Boolean).join("\n");
  }

  const root = document.createElement("div");
  root.innerHTML = baseHtml || "";
  let fileIndex = 0;

  Array.from(root.querySelectorAll("img")).forEach((img) => {
    if (fileIndex >= tags.length) {
      if (!img.getAttribute("src")) img.remove();
      return;
    }

    if (img.getAttribute("src")) {
      // This image is already represented by the rich clipboard payload.
      fileIndex += 1;
      return;
    }

    const replacement = htmlNodeFromTag(tags[fileIndex]);
    fileIndex += 1;
    if (replacement) img.replaceWith(replacement);
    else img.remove();
  });

  // Empty image placeholders without a matching File only render broken icons.
  root.querySelectorAll("img:not([src])").forEach((img) => img.remove());

  const remaining = tags.slice(fileIndex);
  if (!remaining.length) return root.innerHTML;
  const separator = root.innerHTML.trim() ? "<br>" : "";
  return `${root.innerHTML}${separator}${remaining.join("\n")}`;
}

export async function buildClipboardInsertion({ richHtml, plainText, imageFiles }) {
  const base = richHtml
    ? normalizeClipboardHtml(richHtml)
    : plainTextToHtml(plainText || "");
  const imageTags = await buildInlineImgTagListFromFiles(imageFiles || []);
  return mergeImageTagsIntoClipboardHtml(base, imageTags);
}
